import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { authService } from './services/authService';
import Logo from './assets/logo-2.png';
import { Bot, Sparkles, Zap, ArrowRight } from 'lucide-react';

// Feature highlights for the branding side
const features = [
  { icon: Bot, text: 'AI-Powered Automation', color: 'text-blue-600' },
  { icon: Sparkles, text: 'Intelligent Workflows', color: 'text-purple-600' },
  { icon: Zap, text: 'Instant Results', color: 'text-amber-600' },
];

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, checkAuthStatus } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/studio/tools');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    
    try {
      const result = await authService.signIn({
        username: username,
        password: password
      });
      
      if (result.isSignedIn) {
        // Check auth status to update context
        await checkAuthStatus();
        navigate('/studio/tools');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Left: Login Form */}
      <div className="flex flex-col lg:flex-1 w-full lg:w-1/2 bg-white relative">
        {/* Logo */}
        <div className="absolute top-6 left-6 lg:static lg:mt-8 lg:ml-12">
          <img src={Logo} alt="AgenticGenie Logo" className="h-8 w-auto" />
        </div>

        {/* Centered login content */}
        <div className="flex flex-1 flex-col justify-start lg:justify-center items-center px-6 lg:px-0 w-full pt-20 lg:pt-0">
          {/* Heading */}
          <div className="text-center mb-8 lg:mb-12">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Welcome to AgenticGenie
            </h1>
            <p className="text-lg text-gray-600 max-w-md">
              Your intelligent automation platform powered by AI
            </p>
                </div>

          {/* Login Form */}
          <div className="w-full max-w-sm">
            <form onSubmit={handleLogin} className="space-y-6">
                <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                  <input
                    id="username"
                    type="text"
                    value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Enter your username"
                    required
                  />
                </div>

                <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Enter your password"
                    required
                  />
                </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

                <button
                  type="submit"
                disabled={isSubmitting || isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isSubmitting || isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={18} />
                  </>
                )}
                </button>
              </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Secure login powered by AWS Cognito
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Branding & Features */}
      <div className="flex-1 w-full lg:w-1/2 bg-gradient-to-br from-blue-600 to-purple-700 relative flex flex-col items-center justify-center overflow-hidden min-h-[350px] lg:min-h-screen">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 bg-white rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-white rounded-full blur-xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-white rounded-full blur-2xl"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-8 max-w-md">
          <div className="mb-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Unlock AI Power
            </h2>
            <p className="text-lg text-blue-100">
              Transform your workflows with intelligent automation
            </p>
          </div>

          {/* Feature Icons */}
          <div className="space-y-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="flex items-center gap-4 text-white">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <Icon size={24} className="text-white" />
                  </div>
                  <span className="text-lg font-medium">{feature.text}</span>
                </div>
              );
            })}
        </div>
      </div>
      
        {/* Footer */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-center">
          <p className="text-blue-200 text-sm">
            © {new Date().getFullYear()} AgenticGenie
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;